import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Assignee, LeadAttribution, LeadDetail, LeadList, LeadStatus, LeadSummary } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, IconButton, SelectField, Skeleton, Tabs, TextAreaField, TextField, tabId, tabPanelId, useToast, type Tone } from '@agency/ui';
import { ApiClientError, get, post } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtDate, relative, RESTRICTED } from '../../lib/format.ts';
import { ConflictNotice, QueryError } from '../../lib/query-state.tsx';
import { RecordWorkPanel } from './crm-work.tsx';

const statusTone: Record<LeadStatus, Tone> = { NEW: 'info', QUALIFIED: 'success', PROPOSAL: 'accent', WON: 'success', LOST: 'neutral', SPAM: 'warning' };
const statusLabel: Record<LeadStatus, string> = { NEW: 'New', QUALIFIED: 'Qualified', PROPOSAL: 'Proposal', WON: 'Won', LOST: 'Lost', SPAM: 'Spam' };
const VIEWS: { id: string; label: string; status?: LeadStatus; assignee?: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'mine', label: 'My leads', assignee: 'me' },
  { id: 'new', label: 'New', status: 'NEW' },
  { id: 'qualified', label: 'Qualified', status: 'QUALIFIED' },
  { id: 'proposal', label: 'Proposal', status: 'PROPOSAL' },
  { id: 'won', label: 'Won', status: 'WON' },
  { id: 'spam', label: 'Spam', status: 'SPAM' },
];

function exactAmount(value: string): string | null {
  if (!/^(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/.test(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  const fixed = `${whole}.${fraction.padEnd(2, '0')}`;
  return fixed === '0.00' ? null : fixed;
}

export function LeadsPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const view = VIEWS.find((v) => v.id === (params.get('view') ?? 'all')) ?? VIEWS[0]!;
  const search = params.get('q') ?? '';
  const selected = params.get('lead');
  const [draftSearch, setDraftSearch] = useState(search);
  useEffect(() => setDraftSearch(search), [search]);

  // Debounced URL-persisted search.
  useEffect(() => {
    if (draftSearch === search) return;
    const t = setTimeout(() => setParams((p) => { const n = new URLSearchParams(p); draftSearch ? n.set('q', draftSearch) : n.delete('q'); n.delete('lead'); return n; }, { replace: true }), 300);
    return () => clearTimeout(t);
  }, [draftSearch, search, setParams]);

  const query = useInfiniteQuery<LeadList, ApiClientError>({
    queryKey: ['leads', view.id, search],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: '25' });
      if (view.status) p.set('status', view.status);
      if (view.assignee) p.set('assignee', view.assignee);
      if (search) p.set('q', search);
      if (pageParam) p.set('cursor', String(pageParam));
      return get<LeadList>(`/leads?${p}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const counts = query.data?.pages[0]?.counts ?? {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  // Unavailable is not zero: counts only show once the query has actually succeeded.
  const countsKnown = query.isSuccess;

  const setSelected = (id: string | null) => setParams((p) => { const n = new URLSearchParams(p); id ? n.set('lead', id) : n.delete('lead'); return n; });
  const setView = (id: string) => setParams((p) => { const n = new URLSearchParams(p); id === 'all' ? n.delete('view') : n.set('view', id); n.delete('lead'); return n; });
  const idx = items.findIndex((l) => l.id === selected);
  const filtered = Boolean(search) || view.id !== 'all';

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Leads</h1>
          <p>Inquiries from your published landings, with the exact page version each came from.</p>
        </div>
      </div>
      <div className="views" role="group" aria-label="Views">
        {VIEWS.map((v) => (
          <button key={v.id} type="button" className="view" aria-pressed={v.id === view.id} onClick={() => setView(v.id)}>
            {v.label}
            {v.status ? <span className="view__n">{countsKnown ? counts[v.status] ?? 0 : '–'}</span> : v.id === 'all' && !filtered ? <span className="view__n">{countsKnown ? total : '–'}</span> : null}
          </button>
        ))}
      </div>
      <div className="toolbar">
        <div className="toolbar__search">
          <TextField label="Search leads" type="search" value={draftSearch} onChange={(e) => setDraftSearch(e.target.value)} placeholder={can('leads.pii.read') ? 'Name, email or company' : 'Search is limited by your role'} disabled={!can('leads.pii.read')} />
        </div>
      </div>

      {query.isLoading ? (
        <LeadsSkeleton />
      ) : query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        filtered ? (
          <EmptyState title="No leads match these filters" action={<Button onClick={() => { setDraftSearch(''); setParams({}); }}>Reset filters</Button>}>Try another view or clear the search.</EmptyState>
        ) : (
          <EmptyState title="No leads yet">When a visitor submits an inquiry on a published landing, it appears here within seconds.</EmptyState>
        )
      ) : (
        <>
          <div className="ag-table-wrap">
            <table className="ag-table ag-table--responsive">
              <caption className="ag-visually-hidden">Leads, newest first</caption>
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Source</th>
                  <th scope="col">Landing</th>
                  <th scope="col">Status</th>
                  <th scope="col">Owner</th>
                  <th scope="col">Received</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className="ag-row" data-selected={l.id === selected || undefined}>
                    <td data-col="person">
                      <button type="button" className="ag-rowlink" onClick={() => setSelected(l.id)}>
                        <span className="ag-cell-2">
                          <span>{l.piiRedacted ? RESTRICTED : l.name ?? '—'}</span>
                          <span>{l.piiRedacted ? 'Contact details hidden for your role' : [l.company, l.email].filter(Boolean).join(' · ')}</span>
                        </span>
                      </button>
                    </td>
                    <td data-col="source"><span className={l.source.startsWith('not recorded') ? 'ag-muted' : undefined} title={l.source.startsWith('not recorded') ? 'No analytics consent was given, so no campaign data was stored.' : undefined}>{l.source.startsWith('not recorded') ? 'Not recorded' : l.source}</span></td>
                    <td data-col="landing"><span className="ag-cell-2"><span>{l.landingName}</span><span className="ag-mono">v{l.landingVersionNumber}</span></span></td>
                    <td data-col="status"><Badge tone={statusTone[l.status]}>{statusLabel[l.status]}</Badge></td>
                    <td data-col="owner">{l.assigneeName ?? <span style={{ color: 'var(--c-text-2)' }}>Unassigned</span>}</td>
                    <td data-col="received" className="ag-muted" title={fmtDate(l.createdAt)}>{relative(l.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pager">
            <span aria-live="polite">Showing {items.length} lead{items.length === 1 ? '' : 's'}</span>
            {query.hasNextPage ? <Button onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>Load more</Button> : null}
          </div>
        </>
      )}

      <LeadDrawer
        id={selected}
        onClose={() => setSelected(null)}
        onPrev={idx > 0 ? () => setSelected(items[idx - 1]!.id) : undefined}
        onNext={idx >= 0 && idx < items.length - 1 ? () => setSelected(items[idx + 1]!.id) : undefined}
        onChanged={() => void qc.invalidateQueries({ queryKey: ['leads'] })}
      />
    </>
  );
}

function LeadsSkeleton() {
  return (
    <div className="ag-table-wrap" role="status"><span className="ag-visually-hidden">Loading leads…</span>
      <table className="ag-table">
        <tbody>
          {Array.from({ length: 6 }).map((_, i) => (
            <tr key={i}>
              <td><span className="ag-cell-2"><Skeleton width={140} /><Skeleton width={200} height={10} /></span></td>
              <td><Skeleton width={90} /></td>
              <td><Skeleton width={110} /></td>
              <td><Skeleton width={64} height={22} /></td>
              <td><Skeleton width={80} /></td>
              <td><Skeleton width={70} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type Tab = 'overview' | 'activity' | 'attribution';

function LeadDrawer({ id, onClose, onPrev, onNext, onChanged }: { id: string | null; onClose: () => void; onPrev?: () => void; onNext?: () => void; onChanged: () => void }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('overview');
  const [conflict, setConflict] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<'' | 'QUALIFIED' | 'LOST' | 'SPAM'>('');
  const [ownerChoice, setOwnerChoice] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  useEffect(() => { setTab('overview'); setConflict(null); setReason(''); setOutcome(''); setOwnerChoice(null); setAmount(''); setCurrency('EUR'); }, [id]);

  const detail = useQuery<LeadDetail, ApiClientError>({ queryKey: ['lead', id], queryFn: () => get<LeadDetail>(`/leads/${id}`), enabled: !!id, refetchInterval: id ? 5000 : false });
  const attribution = useQuery<LeadAttribution, ApiClientError>({ queryKey: ['lead-attribution', id], queryFn: () => get<LeadAttribution>(`/leads/${id}/attribution`), enabled: !!id && tab === 'attribution' && can('leads.pii.read') });
  const assignable = useQuery<Assignee[], ApiClientError>({ queryKey: ['assignable'], queryFn: () => get<Assignee[]>('/users/assignable'), enabled: !!id && can('leads.assign') });
  const d = detail.data;

  // Optimistic edits; any server refusal reverts the cache and explains the specific reason.
  const mutate = useMutation<LeadDetail, ApiClientError, { kind: 'assign' | 'qualify' | 'proposal' | 'won'; body: Record<string, unknown> }, { prev?: LeadDetail }>({
    mutationFn: ({ kind, body }) => post<LeadDetail>(`/leads/${id}/${kind}`, { ...body, expectedVersion: d!.version }),
    onMutate: async ({ kind, body }) => {
      await qc.cancelQueries({ queryKey: ['lead', id] });
      const prev = qc.getQueryData<LeadDetail>(['lead', id]);
      if (prev) {
        qc.setQueryData<LeadDetail>(['lead', id], kind === 'assign'
          ? { ...prev, assigneeId: body.assigneeId as string | null, assigneeName: assignable.data?.find((a) => a.id === body.assigneeId)?.name ?? null }
          : { ...prev, status: (kind === 'qualify' ? body.outcome : kind === 'proposal' ? 'PROPOSAL' : 'WON') as LeadStatus });
      }
      return { prev };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['lead', id], ctx.prev);
      if (err.code === 'VERSION_CONFLICT') setConflict(err.message);
      else toast({ tone: 'error', message: `${err.message}${err.requestId ? ` (Ref ${err.requestId})` : ''}` });
    },
    onSuccess: (fresh, v) => {
      qc.setQueryData(['lead', id], fresh);
      setConflict(null);
      setOwnerChoice(null);
      setOutcome('');
      setReason('');
      setAmount('');
      toast({ tone: 'success', message: v.kind === 'assign' ? (fresh.assigneeName ? `Assigned to ${fresh.assigneeName}.` : 'Lead unassigned.') : v.kind === 'proposal' ? 'Proposal saved.' : `Marked ${statusLabel[fresh.status].toLowerCase()}.` });
      void qc.invalidateQueries({ queryKey: ['deals'] });
      void qc.invalidateQueries({ queryKey: ['pipeline'] });
      onChanged();
    },
  });

  return (
    <Drawer open={!!id} onClose={onClose} title="Lead"
      footer={<><IconButton icon="chevronLeft" label="Previous lead" disabled={!onPrev} onClick={onPrev} /><IconButton icon="chevronRight" label="Next lead" disabled={!onNext} onClick={onNext} /></>}>
      {detail.isLoading ? (
        <div role="status" style={{ display: 'grid', gap: 12 }}><span className="ag-visually-hidden">Loading lead…</span><Skeleton width="60%" height={22} /><Skeleton width="40%" /><Skeleton height={80} /></div>
      ) : detail.isError ? (
        <QueryError error={detail.error} onRetry={() => void detail.refetch()} />
      ) : d ? (
        <>
          <div className="drawer-id">
            <h3>{d.piiRedacted ? RESTRICTED : d.name ?? 'Unnamed'}</h3>
            <div className="drawer-id__row">
              <Badge tone={statusTone[d.status]}>{statusLabel[d.status]}</Badge>
              <span>{d.assigneeName ? `Owned by ${d.assigneeName}` : 'Unassigned'}</span>
              {d.spamScore > 0 ? <Badge tone="warning">Spam score {d.spamScore}</Badge> : null}
            </div>
          </div>
          {conflict ? <ConflictNotice message={conflict} onReload={() => { setConflict(null); void detail.refetch(); }} /> : null}
          <Tabs<Tab> id="lead-tabs" label="Lead sections" value={tab} onChange={setTab} tabs={[{ id: 'overview', label: 'Overview' }, { id: 'activity', label: 'Activity' }, { id: 'attribution', label: 'Attribution' }]} />
          <div role="tabpanel" id={tabPanelId('lead-tabs')} aria-labelledby={tabId('lead-tabs', tab)} style={{ paddingTop: 16 }}>
            {tab === 'overview' ? (
              <>
                <dl className="facts">
                  <dt>Email</dt><dd>{d.piiRedacted ? RESTRICTED : d.email ?? '—'}</dd>
                  <dt>Company</dt><dd>{d.piiRedacted ? RESTRICTED : d.company ?? '—'}</dd>
                  <dt>Received</dt><dd>{fmtDate(d.createdAt)}</dd>
                  <dt>Landing</dt><dd>{d.landingName} <span className="ag-mono">v{d.landingVersionNumber}</span></dd>
                  <dt>Locale</dt><dd>{d.locale}</dd>
                  {d.deal && can('deals.read') ? <><dt>Proposal value</dt><dd>{d.deal.amount} {d.deal.currency}</dd><dt>Proposed</dt><dd>{fmtDate(d.deal.proposedAt)}</dd>{d.deal.wonAt ? <><dt>Won</dt><dd>{fmtDate(d.deal.wonAt)}</dd></> : null}</> : null}
                </dl>
                <h3 className="drawer-section">Message</h3>
                <div className="message-box">{d.piiRedacted ? RESTRICTED : d.message ?? '—'}</div>
                {(can('leads.assign') || can('leads.qualify') || can('deals.edit')) ? (
                  <>
                    <h3 className="drawer-section">Actions</h3>
                    <div className="actions">
                      {can('leads.assign') ? (
                        <div className="actions__row">
                          <div style={{ flex: 1, minWidth: 160 }}>
                            <SelectField label="Owner" value={ownerChoice ?? d.assigneeId ?? ''} disabled={assignable.isLoading}
                              onChange={(e) => setOwnerChoice(e.target.value)}
                              error={assignable.isError ? 'Could not load team members.' : undefined}>
                              <option value="">Unassigned</option>
                              {assignable.data?.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </SelectField>
                          </div>
                          <Button loading={mutate.isPending} aria-disabled={ownerChoice === null || (ownerChoice || null) === d.assigneeId || undefined} onClick={() => { if (ownerChoice !== null && (ownerChoice || null) !== d.assigneeId) mutate.mutate({ kind: 'assign', body: { assigneeId: ownerChoice || null } }); }}>Assign</Button>
                        </div>
                      ) : null}
                      {can('leads.qualify') && (d.status === 'NEW' || d.status === 'QUALIFIED' || d.status === 'PROPOSAL') ? (
                        <>
                          <SelectField label="Qualification" value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
                            <option value="">Choose an outcome…</option>
                            {d.status === 'NEW' ? <option value="QUALIFIED">Qualified</option> : null}
                            <option value="LOST">Lost</option>
                            {d.status !== 'PROPOSAL' ? <option value="SPAM">Spam</option> : null}
                          </SelectField>
                          <TextAreaField label="Reason (optional)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
                          <div><Button variant="primary" loading={mutate.isPending} aria-disabled={!outcome || undefined} onClick={() => outcome && mutate.mutate({ kind: 'qualify', body: { outcome, reason: reason || undefined } })}>Apply</Button></div>
                        </>
                      ) : null}
                      {can('deals.edit') && (d.status === 'QUALIFIED' || d.status === 'PROPOSAL') ? (
                        <div>
                          <h4>{d.status === 'PROPOSAL' ? 'Update proposal' : 'Create proposal'}</h4>
                          <div className="actions__row">
                            <TextField label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={d.deal?.amount ?? '35000.00'} />
                            <SelectField label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                              {['EUR', 'USD', 'GBP', 'CHF', 'RON', 'AED'].map((code) => <option key={code} value={code}>{code}</option>)}
                            </SelectField>
                          </div>
                          <Button loading={mutate.isPending} aria-disabled={!exactAmount(amount) || undefined} onClick={() => { const fixed = exactAmount(amount); if (fixed) mutate.mutate({ kind: 'proposal', body: { amount: fixed, currency } }); }}>Save proposal</Button>
                        </div>
                      ) : null}
                      {can('deals.edit') && d.status === 'PROPOSAL' && d.deal ? (
                        <div><Button variant="primary" loading={mutate.isPending} onClick={() => mutate.mutate({ kind: 'won', body: {} })}>Mark deal won</Button></div>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
            {tab === 'activity' ? <RecordWorkPanel target={{ targetType: 'LEAD', targetId: d.id }} systemEvents={d.events} /> : null}
            {tab === 'attribution' ? (
              <>
                <dl className="facts">
                  <dt>Analytics consent</dt><dd>{d.consent.analytics ? 'Given' : 'Not given'}</dd>
                  <dt>Advertising consent</dt><dd>{d.consent.advertising ? 'Given' : 'Not given'}</dd>
                  <dt>Policy version</dt><dd className="ag-mono">{d.consent.policyVersion}</dd>
                  <dt>Landing version</dt><dd className="ag-mono">{d.landingVersionId}</dd>
                  <dt>Form version</dt><dd className="ag-mono">{d.formVersionId}</dd>
                </dl>
                <h3 className="drawer-section">First touch</h3>
                {d.firstTouch ? <dl className="facts">{Object.entries(d.firstTouch).map(([k, v]) => (<><dt key={`${k}-k`}>{k}</dt><dd key={`${k}-v`}>{v}</dd></>))}</dl> : <p style={{ color: 'var(--c-text-2)' }}>Not recorded. The visitor did not give analytics consent, or arrived without campaign context. This lead is unattributed, not “organic”.</p>}
                {can('leads.pii.read') ? <>
                  <h3 className="drawer-section">Visitor journey</h3>
                  {attribution.isLoading ? <Skeleton width="100%" height={80} /> : attribution.isError ? <QueryError error={attribution.error} onRetry={() => void attribution.refetch()} /> : !attribution.data?.visitorLinked ? <p>No linked visitor journey was recorded for this inquiry.</p> :
                    <ol>{attribution.data.touches.map((touch) => <li key={touch.id} style={{ marginBottom: 12 }}>
                      <strong>{touch.landingName}</strong> · {touch.channel} · {fmtDate(touch.occurredAt)}
                      {touch.utmSource ? ` · ${touch.utmSource}${touch.utmMedium ? ` / ${touch.utmMedium}` : ''}` : ''}
                      {touch.utmCampaign ? ` · ${touch.utmCampaign}` : ''}
                      {touch.first ? ' · First' : ''}{touch.last ? ' · Last' : ''}{touch.creation ? ' · Created lead' : ''}
                    </li>)}</ol>}
                </> : null}
              </>
            ) : null}
          </div>
        </>
      ) : null}
    </Drawer>
  );
}
